import QRCode from 'qrcode';

/**
 * リカバリ用 QR 焼込み helper (GAP-U7、Phase 5 L2)。
 *
 * 旧 `apps/user/src/lib/recovery.ts` (`generateQrDataUrl` + `buildRecoveryUrl`) を踏襲。
 * docs/97 §3 — L2 引用 (errorCorrectionLevel='H'、地区名 caption 焼込み、中央ロゴ非採用)。
 *
 * 差分:
 *   - SSR safe: `document` 不在時は QR 単体 dataURL を返す
 *   - generateQrDataUrl は Promise<string> ('image/png' base64 dataURL)
 *   - 2026-09-04: QR の下に**複数行**の情報 (ニックネーム / 参加セッション / 復帰コード) を
 *     焼き込めるようにした (`lines`)。スクリーンショットで保存された画像だけを見て
 *     「誰のどの参加セッションの復帰用か」が分かるようにするため。QR が符号化する URL は一切変えない。
 */

/**
 * 復帰用 deep link を生成。
 *  /m/:urlToken/recovery/auto?c=:code
 *
 * urlToken / code は encodeURIComponent で安全化。
 */
export function buildRecoveryUrl(urlToken: string, code: string): string {
  const origin =
    typeof window !== 'undefined' && window.location?.origin ? window.location.origin : '';
  return `${origin}/m/${encodeURIComponent(urlToken)}/recovery/auto?c=${encodeURIComponent(code)}`;
}

export interface GenerateQrOptions {
  /** 上部に焼き込む地区名等。空文字は QR 単体扱い。 */
  caption?: string;
  /** 下部に焼き込む参加コード等 (等幅、`lines` より下)。 */
  subCaption?: string;
  /**
   * QR と subCaption の間に焼き込む説明行 (ニックネーム / 参加セッション等)。
   * 空文字・undefined は除外。長い行は描画幅に収まるよう自動で縮小される。
   */
  lines?: (string | undefined | null)[];
}

/** 焼き込む情報が 1 つも無いか (QR 単体で足りるか)。 */
function isPlain(opts?: GenerateQrOptions): boolean {
  const hasLines = (opts?.lines ?? []).some((l) => !!l && l.trim().length > 0);
  return !opts?.caption?.trim() && !opts?.subCaption?.trim() && !hasLines;
}

/**
 * QR (errorCorrectionLevel='H') を生成。
 * caption / lines / subCaption 指定時は canvas で結合。document 不在時は QR 単体に fallback。
 */
export async function generateQrDataUrl(text: string, opts?: GenerateQrOptions): Promise<string> {
  const caption = opts?.caption?.trim();
  const subCaption = opts?.subCaption?.trim();
  const lines = (opts?.lines ?? [])
    .map((l) => (l ?? '').trim())
    .filter((l): l is string => l.length > 0);

  if (isPlain(opts) || typeof document === 'undefined') {
    return QRCode.toDataURL(text, { width: 240, margin: 2, errorCorrectionLevel: 'H' });
  }

  const qrSize = 240;
  const qrCanvas = document.createElement('canvas');
  await QRCode.toCanvas(qrCanvas, text, {
    width: qrSize,
    margin: 2,
    errorCorrectionLevel: 'H',
  });

  const padX = 16;
  const captionH = caption ? 40 : 0;
  const lineH = 22;
  const linesH = lines.length > 0 ? lines.length * lineH + 6 : 0;
  const subCaptionH = subCaption ? 36 : 0;
  const totalW = qrSize + padX * 2;
  const totalH = captionH + qrSize + linesH + subCaptionH + 8;

  const out = document.createElement('canvas');
  out.width = totalW;
  out.height = totalH;
  const ctx = out.getContext('2d');
  if (!ctx) {
    return qrCanvas.toDataURL('image/png');
  }

  ctx.fillStyle = '#ffffff';
  ctx.fillRect(0, 0, totalW, totalH);

  if (caption) {
    ctx.fillStyle = '#0F766E';
    ctx.font =
      'bold 18px system-ui, -apple-system, "Segoe UI", "Hiragino Sans", "Yu Gothic", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(caption, totalW / 2, captionH / 2 + 4, totalW - padX * 2);
  }

  ctx.drawImage(qrCanvas, padX, captionH, qrSize, qrSize);

  // ニックネーム / 参加セッション等。QR の直下、復帰コードの上。
  if (lines.length > 0) {
    ctx.fillStyle = '#1f2937';
    ctx.font =
      '14px system-ui, -apple-system, "Segoe UI", "Hiragino Sans", "Yu Gothic", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    lines.forEach((line, i) => {
      // maxWidth を渡すと canvas 側が自動で横方向に圧縮する (折返しはしない)。
      ctx.fillText(
        line,
        totalW / 2,
        captionH + qrSize + 8 + i * lineH + lineH / 2,
        totalW - padX * 2,
      );
    });
  }

  if (subCaption) {
    ctx.fillStyle = '#404040';
    ctx.font = '14px ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(
      subCaption,
      totalW / 2,
      captionH + qrSize + linesH + subCaptionH / 2 + 4,
      totalW - padX * 2,
    );
  }

  return out.toDataURL('image/png');
}

/**
 * 復帰用 QR 画像に焼き込む「本人が分かる情報」を組み立てる (pure)。
 *
 * 画像だけを見て「誰の・どの参加セッションの復帰用か」が分かるようにするための行。
 * 値が無いものは行ごと省く (空行を作らない)。
 *
 * @param nickname      参加者のニックネーム
 * @param sessionNames  参加セッションの名前 (0 件なら行を出さない)
 */
export function buildRecoveryQrLines(
  nickname: string | null | undefined,
  sessionNames: readonly string[],
): string[] {
  const lines: string[] = [];
  const nick = (nickname ?? '').trim();
  if (nick) lines.push(`👤 ${nick}`);
  const names = sessionNames.map((n) => n.trim()).filter((n) => n.length > 0);
  if (names.length > 0) lines.push(`🚶 ${names.join(' / ')}`);
  return lines;
}

/**
 * 画像に焼き込んだ情報の署名 (pure)。
 *
 * ニックネームや参加セッションが変わったかどうかを、表示中の画像と現在値で比較するために使う。
 * セッションは選んだ順に依存しないよう並べ替えてから連結する。
 */
export function recoveryQrSignature(
  nickname: string | null | undefined,
  sessionNames: readonly string[],
): string {
  const nick = (nickname ?? '').trim();
  const names = sessionNames
    .map((n) => n.trim())
    .filter((n) => n.length > 0)
    .slice()
    .sort();
  return JSON.stringify([nick, names]);
}
